<template>
  <div
    :class="['c-asset__filepicker', dragging ? 'c-asset__filepicker--dragover' : '']"
    @drop.prevent="droppedFile"
    @dragover.prevent="dragging = true"
    @dragleave="dragging = false"
  >
    <label
      v-show="!upload.loading && !upload.err"
      :for="`${randomID}-browse`"
      class="u-color-disabled"
    >
      {{ t('filePicker') }}
    </label>
    <div v-if="upload.loading && file">
      <!-- file info -->
      <div>
        <code class="c-asset__file--metadata">
          {{ file.name }}
          <span class="u-color-disabled">({{ formatBytes(file.size) }})</span>
        </code>
      </div>
      <!-- progress bar -->
      <div>
        <div v-show="progress === 100" class="u-color-disabled">
          <small>{{ t('processingUploaded') }}</small>
        </div>
        <!--
          max > 100 because there is an extra time after the upload is complete.
          This is just to show the user that the upload is not totally complete yet.
        -->
        <progress max="105" :value="progress">{{ progress }}</progress>
      </div>
    </div>
    <div v-if="upload.err" class="u-color-error">
      {{ upload.err.apiMessage || t('unknownError') }}
      {{ t('removeAndRetry') }}
    </div>

    <input
      v-show="upload.loading && !upload.err"
      :id="`${randomID}-browse`"
      ref="input"
      name="browse"
      type="file"
      :disabled="disabled"
      @change="browsedFile"
    >

    <div class="c-asset__file-actions">
      <!-- delete uploaded file with error -->
      <button
        v-if="upload.err"
        type="button"
        :title="t('remove')"
        class="c-button c-button--medium c-button--transparent
               c-asset__file-actions--button u-color-error"
        @click.prevent="$emit('remove')"
      >
        <SVGIcon class="c-button__icon" name="cancel-square" />
      </button>
      <!-- abort ongoing upload -->
      <button
        v-if="upload.loading"
        type="button"
        :title="t('cancel')"
        class="c-button c-button--medium c-button--transparent
               c-asset__file-actions--button u-color-error"
        @click.prevent="abort"
      >
        <SVGIcon class="c-button__icon" name="cancel-square" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from '@/utils/i18n'
import { useRequest, UseRequest, Asset, uploadAsset, AbortError } from '@/utils/api'
import { formatBytes } from '@/utils/format-bytes'
import SVGIcon from '@/components/SVGIcon.vue'
import type { AssetFieldStatus } from '@/components/ProjectAssetsField.vue'
import { getCookie } from '@/utils/get-cookie'

const props = defineProps<{
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'updateStatus', val: AssetFieldStatus): void
  (e: 'successUpload', val: Asset): void
  (e: 'remove'): void
}>()

const input = ref<HTMLInputElement>()
const randomID = Math.random().toString(36).substr(2, 9)
const file = ref<File>()
const dragging = ref(false)
const progress = ref(0)

const isDisabled = ref(props.disabled)

onMounted(() => {
  isDisabled.value = props.disabled
})

let controller: AbortController | null = null
const abort = () => { controller?.abort() }

const csrftoken = getCookie('csrftoken')!

// const sessionid = getCookie('ircamauth_sessionid')!
// console.log('COOKIE ircamauth_sessionid: ', sessionid)

const isDevMode = import.meta.env.MODE === 'development'
console.log('IS DEV MODE: ', isDevMode)

let upload: UseRequest<void>

// Upload using nginx reverse proxy in production / staging mode only
if (!isDevMode) {
  upload = useRequest(async () => {
    controller = new AbortController()
    emit('updateStatus', 'uploading')

    const fd = new FormData()
    fd.append('file', file.value!)

    progress.value = 0
    try {
      const asset = await uploadAsset(fd, {
        onProgress: (e) => { progress.value = Math.round((e.loaded * 100) / e.total) },
        signal: controller.signal
      })
      console.log('ASSET UPLOADED: ', asset)
      controller = null
      emit('successUpload', asset)
    } catch (e) {
      // discard abort error
      if (e instanceof AbortError) { return onAbort() }
      throw e
    }
  }, {
    error () {
      emit('updateStatus', 'error')
    }
  })
} else {
  // Bypass the nginx proxy in dev mode, upload FormData to Forum CMS directly
  upload = useRequest(async () => {
    controller = new AbortController()
    emit('updateStatus', 'uploading')

    const fd = new FormData()
    fd.append('file', file.value!)
    fd.append('name', String(file.value?.name))
    fd.append('size', String(file.value?.size))
    fd.append('type', String(file.value?.type))

    progress.value = 0

    try {
      const url = '/api/project-assets/'

      await fetch(url, {
        credentials: 'include',
        method: 'POST',
        mode: 'same-origin',
        headers: {
          'X-CSRFToken': csrftoken
        },
        body: fd
      }).then(async response => {
        if (response.status === 200) {
          progress.value = 105

          await response.json().then(async data => {
            controller = null

            const asset = {
              pk: data.pk,
              order: data.order,
              name: data.name,
              size: data.size,
              content_type: data.type,
              file: data.file,
              path: data.path,
              url: data.url,
              access_url: data.access_url,
              hash: data.hash,
              uuid: data.uuid,
              label: data.label,
              is_premium: data.is_premium,
              authorized_download: data.authorized_download,
              asset_meta: data.asset_meta,
              asset_group: data.asset_group
            }

            console.log(asset)

            emit('successUpload', data)
          })
        } else {
          response.text().then(text => {
            throw new Error(text)
          })
        }
      }).catch(error => {
        console.log(error)
      }).finally(() => {
        // assets.push(asset)
        // console.log('assets: ', assets)

        // emit('updateAssets', assets)
      })
    } catch (e) {
      // discard abort error
      if (e instanceof AbortError) { return onAbort() }
      throw e
    }
  }, {
    error () {
      emit('updateStatus', 'error')
    }
  })
}

function droppedFile (e: DragEvent) {
  dragging.value = true
  file.value = e.dataTransfer!.files[0]!
  upload.exec()
}

function browsedFile (e: Event) {
  const input = e.target as HTMLInputElement
  file.value = input.files![0]
  upload.exec()
}

function onAbort () {
  dragging.value = false
  progress.value = 0
  file.value = undefined
  if (input.value !== undefined) {
    input.value.value = ''
  }
  emit('updateStatus', 'initial')
}

const { t } = useI18n({
  messages: {
    en: {
      filePicker: 'Click to browse, or drop the file you want to add to the release.',
      processingUploaded: 'Processing the uploaded file...',
      removeAndRetry: 'Please remove it and retry.',
      unknownError: 'An error occurred while uploading the asset.',
      cancel: 'Cancel the upload',
      remove: 'Remove'
    },
    fr: {
      filePicker: 'Cliquer pour parcourir, ou déposez le fichier que vous souhaitez ajouter à la release.',
      processingUploaded: 'Traitement du fichier téléversé...',
      removeAndRetry: 'Merci de le supprimer puis de réessayer.',
      unknownError: 'Une erreur est survenue lors du téléversement.',
      cancel: 'Annuler le téléversement',
      remove: 'Supprimer'
    }
  }
})
</script>
